import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';

interface Gift {
    id: string;
    code: string;
    name: string;
    description?: string | null;
    image_url?: string | null;
    status: string;
    total_quantity: number;
    remaining_quantity?: number;
    reserved_quantity: number;
    assigned_quantity: number;
    claimed_quantity: number;
    win_probability?: number;
    daily_limit?: number | null;
    valid_from?: string | null;
    valid_until?: string | null;
    metadata: Record<string, any>;
    tier?: string;
    tier_weight?: number;
    campaign_id: string;
    campaign?: { id: string; name: string };
    gift_group?: { id: string; name: string } | null;
    created_at: string;
    updated_at: string;
}

interface GiftFormData {
    name: string;
    code: string;
    description: string;
    campaign_id: string;
    total_quantity: number;
    win_probability: number;
    daily_limit: string;
    use_custom_dates: boolean;
    valid_from: string;
    valid_until: string;
    status: string;
    image_url: string;
    tier: string;
    tier_weight: number;
    metadata: {
        type: string;
        value: string;
        terms: string;
    };
}

interface CampaignOption {
    id: string;
    name: string;
}

interface GiftDistribution {
    id: string;
    status: string;
    reward_code?: string | null;
    assigned_at?: string | null;
    claimed_at?: string | null;
    expires_at?: string | null;
    player?: {
        full_name?: string | null;
        phone?: string | null;
        email?: string | null;
    } | null;
    play_session?: {
        id: string;
        score?: number | null;
        started_at?: string | null;
        finished_at?: string | null;
    } | null;
}

const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    out_of_stock: 'bg-red-100 text-red-800',
    archived: 'bg-yellow-100 text-yellow-800',
};

const giftTypeLabels: Record<string, string> = {
    voucher: 'Voucher',
    physical: 'Vật lý',
    digital: 'Kỹ thuật số',
    coupon: 'Phiếu giảm giá',
    default: 'Quà tặng',
};

const emptyForm: GiftFormData = {
    name: '',
    code: '',
    description: '',
    campaign_id: '',
    total_quantity: 100,
    win_probability: 100,
    daily_limit: '',
    use_custom_dates: false,
    valid_from: '',
    valid_until: '',
    status: 'active',
    image_url: '',
    tier: 'bronze',
    tier_weight: 100,
    metadata: { type: 'voucher', value: '', terms: '' },
};

const TIER_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
    bronze:   { label: 'Bronze',   color: 'bg-amber-100 text-amber-800 border-amber-300',  icon: '🥉' },
    silver:   { label: 'Silver',   color: 'bg-gray-100 text-gray-700 border-gray-300',     icon: '🥈' },
    gold:     { label: 'Gold',     color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '🥇' },
    platinum: { label: 'Platinum', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: '💎' },
};

const GIFTS_POLL_MS = 5000;
const INVENTORY_EVENT_CHANNEL = 'xgame:inventory-events';
const INVENTORY_EVENT_STORAGE_KEY = 'xgame:lastInventoryEvent';

type InventoryEventPayload = {
    type?: string;
    campaignId?: string;
    campaignSlug?: string;
    giftId?: string;
    rewardCode?: string;
    at?: number;
};

function dateToInput(value?: string | null) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

function inputToIso(value: string, endOfDay = false) {
    if (!value) return null;
    return `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`;
}

function formatDate(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

function formatDateTime(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function autoCode(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 128);
}

function shouldRefreshForInventoryEvent(payload: InventoryEventPayload | null, currentCampaignId: string) {
    if (!payload || payload.type !== 'reward-assigned') return false;
    if (!currentCampaignId) return true;

    // Some game runtimes only know the public campaign slug. Refresh rather than risking stale inventory.
    return !payload.campaignId || payload.campaignId === currentCampaignId;
}

function parseInventoryEvent(value: unknown): InventoryEventPayload | null {
    if (!value) return null;
    if (typeof value === 'object') return value as InventoryEventPayload;
    if (typeof value !== 'string') return null;

    try {
        return JSON.parse(value) as InventoryEventPayload;
    } catch {
        return null;
    }
}

export default function Gifts() {
    const [searchParams] = useSearchParams();
    const initialCampaignId = searchParams.get('campaignId') || '';
    const [gifts, setGifts] = useState<Gift[]>([]);
    const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [filterCampaign, setFilterCampaign] = useState(initialCampaignId);
    const [form, setForm] = useState<GiftFormData>({ ...emptyForm });
    const [distributionGift, setDistributionGift] = useState<Gift | null>(null);
    const [distributions, setDistributions] = useState<GiftDistribution[]>([]);
    const [distributionLoading, setDistributionLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [showAdvancedDist, setShowAdvancedDist] = useState(false);
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    const fetchData = useCallback(async () => {
        try {
            setError('');
            const [giftData, campaignData] = await Promise.all([
                api.getGifts(tenantId, filterCampaign || undefined, 1, 100),
                api.getCampaigns(tenantId, 1, 100),
            ]);

            setGifts(parsePaginatedResponse<Gift>(giftData).items);
            setCampaigns(parsePaginatedResponse<any>(campaignData).items.map((campaign: any) => ({
                id: campaign.id,
                name: campaign.name,
            })));
        } catch (err: any) {
            console.error('Failed to fetch gifts:', err);
            setError(err.message || 'Failed to load gifts');
        } finally {
            setLoading(false);
        }
    }, [tenantId, filterCampaign]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!tenantId) return;

        const interval = setInterval(() => {
            fetchData();
        }, GIFTS_POLL_MS);

        return () => clearInterval(interval);
    }, [tenantId, fetchData]);

    useEffect(() => {
        if (!tenantId) return;

        const refreshOnReturn = () => {
            fetchData();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshOnReturn();
            }
        };

        window.addEventListener('focus', refreshOnReturn);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', refreshOnReturn);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [tenantId, fetchData]);

    useEffect(() => {
        if (!tenantId) return;

        const refreshFromPayload = (payload: unknown) => {
            const event = parseInventoryEvent(payload);
            if (shouldRefreshForInventoryEvent(event, filterCampaign)) {
                fetchData();
            }
        };

        let channel: BroadcastChannel | null = null;
        if ('BroadcastChannel' in window) {
            channel = new BroadcastChannel(INVENTORY_EVENT_CHANNEL);
            channel.onmessage = (event) => refreshFromPayload(event.data);
        }

        const handleStorage = (event: StorageEvent) => {
            if (event.key === INVENTORY_EVENT_STORAGE_KEY) {
                refreshFromPayload(event.newValue);
            }
        };

        const handleMessage = (event: MessageEvent) => {
            if (event.origin === window.location.origin) {
                refreshFromPayload(event.data);
            }
        };

        window.addEventListener('storage', handleStorage);
        window.addEventListener('message', handleMessage);

        return () => {
            channel?.close();
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('message', handleMessage);
        };
    }, [tenantId, filterCampaign, fetchData]);

    const totals = useMemo(() => {
        return gifts.reduce((acc, gift) => {
            acc.total += gift.total_quantity || 0;
            acc.remaining += getRemaining(gift);
            acc.assigned += gift.assigned_quantity || 0;
            acc.claimed += gift.claimed_quantity || 0;
            return acc;
        }, { total: 0, remaining: 0, assigned: 0, claimed: 0 });
    }, [gifts]);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...emptyForm, campaign_id: filterCampaign });
        setShowAdvancedDist(false);
        setShowModal(true);
    };

    const handleImageUpload = async (file: File) => {
        if (!file) return;
        // Validate client-side trước khi gửi
        if (file.size > 200 * 1024) {
            alert('Ảnh tối đa 200KB. Vui lòng chọn ảnh nhỏ hơn.');
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            alert('Chỉ chấp nhận ảnh JPG, PNG, hoặc WebP.');
            return;
        }
        setUploadingImage(true);
        try {
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('image', file);
            // Gửi URL ảnh cũ để server xóa
            if (form.image_url && form.image_url.includes('/storage/')) {
                formData.append('old_path', form.image_url);
            }
            const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/admin/upload/image`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}`, Accept: 'application/json' } : { Accept: 'application/json' },
                body: formData,
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({ message: 'Upload thất bại' }));
                throw new Error(err.message || 'Upload thất bại');
            }
            const data = await response.json();
            setForm((previous) => ({ ...previous, image_url: data.url }));
        } catch (err: any) {
            alert(err.message || 'Upload thất bại');
        } finally {
            setUploadingImage(false);
        }
    };

    const openEdit = (gift: Gift) => {
        setEditingId(gift.id);
        setForm({
            name: gift.name,
            code: gift.code,
            description: gift.description || '',
            campaign_id: gift.campaign_id,
            total_quantity: gift.total_quantity,
            win_probability: gift.win_probability ?? 100,
            daily_limit: gift.daily_limit ? String(gift.daily_limit) : '',
            use_custom_dates: Boolean(gift.valid_from || gift.valid_until),
            status: gift.status,
            image_url: gift.image_url || '',
            valid_from: dateToInput(gift.valid_from),
            valid_until: dateToInput(gift.valid_until || gift.metadata?.expires_at),
            tier: gift.tier || 'bronze',
            tier_weight: gift.tier_weight ?? 100,
            metadata: {
                type: gift.metadata?.type || 'voucher',
                value: gift.metadata?.value || '',
                terms: gift.metadata?.terms || '',
            },
        });
        setShowAdvancedDist(false);
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name || !form.campaign_id) {
            alert('Vui lòng điền Tên quà và chọn Chiến dịch.');
            return;
        }
        if (form.valid_from && form.valid_until && form.valid_from > form.valid_until) {
            alert('Ngày bắt đầu phải trước ngày kết thúc.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                code: form.code.trim(),
                description: form.description.trim(),
                campaign_id: form.campaign_id,
                tenant_id: tenantId,
                total_quantity: Number(form.total_quantity) || 0,
                win_probability: Number(form.win_probability) || 100,
                daily_limit: form.daily_limit ? parseInt(form.daily_limit, 10) : null,
                status: form.status,
                image_url: form.image_url.trim() || null,
                valid_from: form.use_custom_dates ? inputToIso(form.valid_from) : null,
                valid_until: form.use_custom_dates ? inputToIso(form.valid_until, true) : null,
                tier: form.tier || 'bronze',
                tier_weight: Number(form.tier_weight) || 100,
                metadata: { ...form.metadata },
            };

            if (editingId) {
                await api.updateGift(editingId, {
                    name: payload.name,
                    description: payload.description,
                    total_quantity: payload.total_quantity,
                    win_probability: payload.win_probability,
                    daily_limit: payload.daily_limit,
                    status: payload.status,
                    image_url: payload.image_url,
                    valid_from: payload.valid_from,
                    valid_until: payload.valid_until,
                    tier: payload.tier,
                    tier_weight: payload.tier_weight,
                    metadata: payload.metadata,
                });
            } else {
                await api.createGift(payload);
            }

            setShowModal(false);
            setForm({ ...emptyForm });
            setEditingId(null);
            await fetchData();
        } catch (err: any) {
            console.error('Failed to save gift:', err);
            alert(err.message || 'Failed to save gift');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (gift: Gift) => {
        if (!confirm(`Xóa "${gift.name}"? Thao tác này không thể hoàn tác.`)) return;
        try {
            await api.deleteGift(gift.id);
            await fetchData();
        } catch (err: any) {
            alert(err.message || 'Failed to delete gift');
        }
    };

    const openDistributions = async (gift: Gift) => {
        setDistributionGift(gift);
        setDistributionLoading(true);
        setDistributions([]);
        try {
            const response = await api.getGiftDistributions(gift.id, 1, 100);
            setDistributions(parsePaginatedResponse<GiftDistribution>(response).items);
        } catch (err: any) {
            alert(err.message || 'Failed to load gift usage');
        } finally {
            setDistributionLoading(false);
        }
    };

    function getRemaining(gift: Gift) {
        if (Number.isFinite(gift.remaining_quantity)) {
            return Math.max(0, Number(gift.remaining_quantity));
        }
        return Math.max(0, gift.total_quantity - gift.assigned_quantity - gift.reserved_quantity);
    }

    function getUsagePercent(gift: Gift) {
        return gift.total_quantity > 0
            ? Math.round(((gift.assigned_quantity + gift.reserved_quantity) / gift.total_quantity) * 100)
            : 0;
    }

    function isExpired(gift: Gift) {
        const expiry = gift.valid_until || gift.metadata?.expires_at;
        return Boolean(expiry && new Date(expiry) < new Date());
    }

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Đang tải...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Quà tặng & Phần thưởng</h1>
                    <p className="text-gray-500 mt-1">Tồn kho, hiệu lực, phân bổ và theo dõi nhận quà</p>
                </div>
                <button
                    onClick={openCreate}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                    Thêm quà tặng
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <SummaryCard label="Loại quà" value={gifts.length.toLocaleString()} tone="text-gray-900" />
                <SummaryCard label="Đã nhập" value={totals.total.toLocaleString()} tone="text-blue-600" />
                <SummaryCard label="Còn lại" value={totals.remaining.toLocaleString()} tone="text-green-600" />
                <SummaryCard label="Đã phát / Đã nhận" value={`${totals.assigned.toLocaleString()} / ${totals.claimed.toLocaleString()}`} tone="text-orange-600" />
            </div>

            <div className="mb-4 flex gap-3 items-center">
                <select
                    value={filterCampaign}
                    onChange={(event) => setFilterCampaign(event.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                    <option value="">Tất cả chiến dịch</option>
                    {campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                    ))}
                </select>
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
                {gifts.length === 0 ? (
                    <div className="p-12 text-center">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Chưa có quà tặng</h3>
                        <p className="text-gray-500 mb-4">Tạo quà tặng trước khi cấu hình luật thưởng.</p>
                        <button onClick={openCreate} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                            Tạo quà tặng
                        </button>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <Th>Quà tặng</Th>
                                <Th>Chiến dịch</Th>
                                <Th>Loại</Th>
                                <Th align="center">Đã nhập</Th>
                                <Th align="center">Đã phát</Th>
                                <Th align="center">Đã nhận</Th>
                                <Th align="center">Còn lại</Th>
                                <Th>Hiệu lực</Th>
                                <Th>Trạng thái</Th>
                                <Th>Hành động</Th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {gifts.map((gift) => {
                                const remaining = getRemaining(gift);
                                const usage = getUsagePercent(gift);
                                const expired = isExpired(gift);
                                const type = gift.metadata?.type || 'default';
                                return (
                                    <tr key={gift.id} className={`hover:bg-gray-50 ${expired ? 'opacity-70' : ''}`}>
                                        <td className="px-4 py-3 min-w-[220px]">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">{gift.name}</span>
                                                {gift.tier && TIER_CONFIG[gift.tier] && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded border font-semibold ${TIER_CONFIG[gift.tier].color}`}>
                                                        {TIER_CONFIG[gift.tier].icon} {TIER_CONFIG[gift.tier].label}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400 font-mono">{gift.code}</div>
                                            {gift.metadata?.value && <div className="text-xs text-gray-500 mt-1">{gift.metadata.value}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600 min-w-[180px]">{gift.campaign?.name || '-'}</td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                                {giftTypeLabels[type] || giftTypeLabels.default}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm font-medium">{gift.total_quantity.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-center text-sm text-orange-600 font-medium">{gift.assigned_quantity.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-center text-sm text-green-600 font-medium">{gift.claimed_quantity.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className={`font-bold text-sm ${remaining === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                                                    {remaining.toLocaleString()}
                                                </span>
                                                <div className="w-16 h-1.5 bg-gray-100 rounded-full mt-1">
                                                    <div
                                                        className={`h-1.5 rounded-full ${usage > 90 ? 'bg-red-500' : usage > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                        style={{ width: `${Math.min(usage, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm min-w-[160px]">
                                            <div className="text-gray-600">{formatDate(gift.valid_from)}</div>
                                            <div className={expired ? 'text-red-600 font-medium' : 'text-gray-600'}>{formatDate(gift.valid_until || gift.metadata?.expires_at)}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[gift.status] || 'bg-gray-100 text-gray-800'}`}>
                                                {expired ? 'hết hạn' : gift.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                            <button onClick={() => openDistributions(gift)} className="text-gray-700 hover:text-gray-950 mr-3">
                                                Sdụng
                                            </button>
                                            <button onClick={() => openEdit(gift)} className="text-blue-600 hover:text-blue-800 mr-3">
                                                Sửa
                                            </button>
                                            <button onClick={() => handleDelete(gift)} className="text-red-500 hover:text-red-700">
                                                Xóa
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {editingId ? 'Sửa quà tặng' : 'Quà tặng mới'}
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Chiến dịch *</label>
                                <select
                                    value={form.campaign_id}
                                    onChange={(event) => setForm((previous) => ({ ...previous, campaign_id: event.target.value }))}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    disabled={Boolean(editingId)}
                                >
                                    <option value="">Chọn chiến dịch...</option>
                                    {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Tên *">
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(event) => setForm((previous) => ({
                                            ...previous,
                                            name: event.target.value,
                                            code: !editingId && (previous.code === autoCode(previous.name) || !previous.code)
                                                ? autoCode(event.target.value)
                                                : previous.code,
                                        }))}
                                        placeholder="Voucher 10% Off"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </Field>
                                <Field label="Mã *">
                                    <input
                                        type="text"
                                        value={form.code}
                                        onChange={(event) => setForm((previous) => ({ ...previous, code: event.target.value }))}
                                        placeholder="voucher_10_off"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                        disabled={Boolean(editingId)}
                                    />
                                </Field>
                            </div>

                            <Field label="Mô tả">
                                <textarea
                                    value={form.description}
                                    onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                                    rows={2}
                                    placeholder="Mô tả phần thưởng và cách nhận quà..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </Field>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Loại quà">
                                    <select
                                        value={form.metadata.type}
                                        onChange={(event) => setForm((previous) => ({ ...previous, metadata: { ...previous.metadata, type: event.target.value } }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="voucher">Voucher / Giảm giá</option>
                                        <option value="coupon">Phiếu giảm giá</option>
                                        <option value="digital">Thẻ quà tặng số</option>
                                        <option value="physical">Giải thưởng vật lý</option>
                                    </select>
                                </Field>
                                <Field label="Giá trị (vd: 10%, 50k, Gift Card)">
                                    <input
                                        type="text"
                                        value={form.metadata.value}
                                        onChange={(event) => setForm((previous) => ({ ...previous, metadata: { ...previous.metadata, value: event.target.value } }))}
                                        placeholder="50.000 VND, 10%, Áo thun logo..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </Field>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Số lượng nhập *">
                                    <input
                                        type="number"
                                        value={form.total_quantity}
                                        onChange={(event) => setForm((previous) => ({ ...previous, total_quantity: parseInt(event.target.value, 10) || 0 }))}
                                        min={0}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </Field>
                                <Field label="Trạng thái">
                                    <select
                                        value={form.status}
                                        onChange={(event) => setForm((previous) => ({ ...previous, status: event.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="active">Hoạt động</option>
                                        <option value="inactive">Tạm dừng</option>
                                        <option value="out_of_stock">Hết hàng</option>
                                        <option value="archived">Lưu trữ</option>
                                    </select>
                                </Field>
                            </div>

                            {/* === PHÂN PHỐI THƯỞNG === */}
                            <div className="border border-blue-100 bg-blue-50 rounded-lg p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-blue-800">⚙️ Cấu hình phân phối</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowAdvancedDist(!showAdvancedDist)}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-850 transition-colors"
                                    >
                                        {showAdvancedDist ? '✕ Ẩn cấu hình nâng cao' : '⚙️ Cấu hình nâng cao (Hạng quà)'}
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field label="Xác suất trúng (%)">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    value={form.win_probability}
                                                    onChange={(event) => setForm((previous) => ({ ...previous, win_probability: parseFloat(event.target.value) || 0 }))}
                                                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                                    style={{
                                                        background: `linear-gradient(to right, ${form.win_probability >= 50 ? '#ef4444' : form.win_probability >= 20 ? '#f59e0b' : '#22c55e'} ${form.win_probability}%, #e5e7eb ${form.win_probability}%)`
                                                    }}
                                                />
                                                <div className="flex items-center gap-1 min-w-[80px]">
                                                    <input
                                                        type="number"
                                                        value={form.win_probability}
                                                        onChange={(event) => setForm((previous) => ({ ...previous, win_probability: Math.min(100, Math.max(0, parseFloat(event.target.value) || 0)) }))}
                                                        min={0} max={100} step={1}
                                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm font-bold focus:ring-2 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-500">%</span>
                                                </div>
                                            </div>
                                            <div className="flex justify-between text-xs text-gray-400">
                                                <span>0%</span>
                                                <span className={`font-medium ${form.win_probability >= 50 ? 'text-red-500' : form.win_probability >= 20 ? 'text-amber-500' : 'text-green-600'}`}>
                                                    Trúng: {form.win_probability}% · Thua: {(100 - form.win_probability).toFixed(0)}%
                                                </span>
                                                <span>100%</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1">Xác suất độc lập của quà này. Mỗi quà được roll riêng — tổng có thể &gt; 100%.</p>
                                    </Field>
                                    <Field label="Giới hạn phát / ngày">
                                        <input
                                            type="number"
                                            value={form.daily_limit}
                                            onChange={(event) => setForm((previous) => ({ ...previous, daily_limit: event.target.value }))}
                                            min={1}
                                            placeholder="Không giới hạn"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Số lượng tối đa được phát ra trong ngày. Để trống để phát thoải mái.</p>
                                    </Field>
                                </div>

                                {showAdvancedDist && (
                                    <div className="pt-3 border-t border-blue-100 space-y-4">
                                        {/* TIER SELECTOR */}
                                        <Field label="Hạng quà (Tier)">
                                            <div className="grid grid-cols-4 gap-2">
                                                {Object.entries(TIER_CONFIG).map(([key, cfg]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        onClick={() => setForm(prev => ({ ...prev, tier: key }))}
                                                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 text-xs font-semibold transition-all ${
                                                            form.tier === key ? `${cfg.color} border-current scale-105 shadow-sm` : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <span className="text-lg">{cfg.icon}</span>
                                                        {cfg.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">Bronze = phổ thông · Silver = hiếm · Gold = quý · Platinum = đặc biệt</p>
                                        </Field>

                                        <Field label="Weight trong tier">
                                            <input
                                                type="number"
                                                value={form.tier_weight}
                                                onChange={(event) => setForm((previous) => ({ ...previous, tier_weight: parseInt(event.target.value) || 100 }))}
                                                min={1} max={1000}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Càng cao → xuất hiện nhiều hơn trong tier</p>
                                        </Field>
                                    </div>
                                )}
                            </div>

                            {/* === THỜI GIAN HIỆU LỰC RIÊNG === */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={form.use_custom_dates}
                                        onChange={(event) => setForm((previous) => ({ ...previous, use_custom_dates: event.target.checked }))}
                                        className="w-4 h-4 text-blue-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Đặt thời gian hiệu lực riêng</span>
                                    <span className="text-xs text-gray-400">(mặc định: theo thời gian chiến dịch)</span>
                                </label>
                                {form.use_custom_dates && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                        <Field label="Từ ngày">
                                            <input
                                                type="date"
                                                value={form.valid_from}
                                                onChange={(event) => setForm((previous) => ({ ...previous, valid_from: event.target.value }))}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </Field>
                                        <Field label="Đến ngày">
                                            <input
                                                type="date"
                                                value={form.valid_until}
                                                min={form.valid_from || undefined}
                                                onChange={(event) => setForm((previous) => ({ ...previous, valid_until: event.target.value }))}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                            />
                                        </Field>
                                    </div>
                                )}
                            </div>

                            {/* === ẢNH PHẦN THƯỞNG === */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Hình ảnh phần thưởng</label>
                                <div className="flex gap-3 items-start">
                                    {/* Preview box */}
                                    <div className="flex-shrink-0 w-[100px] h-[100px] border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                                        {form.image_url ? (
                                            <img
                                                src={form.image_url}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                                onError={(e) => { (e.target as HTMLImageElement).src = ''; }}
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-xs text-center px-2">100×100px</span>
                                        )}
                                    </div>
                                    {/* Upload controls */}
                                    <div className="flex-1 space-y-2">
                                        <label className={`flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm w-fit ${
                                            uploadingImage ? 'opacity-50 pointer-events-none' : ''
                                        }`}>
                                            <span>{uploadingImage ? '⏳ Đang xử lý...' : '📁 Chọn ảnh từ máy'}</span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                className="hidden"
                                                disabled={uploadingImage}
                                                onChange={(e) => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0]); }}
                                            />
                                        </label>
                                        <p className="text-xs text-gray-400">JPG/PNG/WebP · Tối đa 200KB · Tự resize về 100×100px</p>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400">hoặc URL:</span>
                                            <input
                                                type="text"
                                                value={form.image_url}
                                                onChange={(event) => setForm((previous) => ({ ...previous, image_url: event.target.value }))}
                                                placeholder="https://..."
                                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                            {form.image_url && (
                                                <button
                                                    type="button"
                                                    onClick={() => setForm((previous) => ({ ...previous, image_url: '' }))}
                                                    className="text-red-400 hover:text-red-600 text-sm"
                                                    title="Xóa ảnh"
                                                >✕</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Field label="Điều kiện áp dụng">
                                <textarea
                                    value={form.metadata.terms}
                                    onChange={(event) => setForm((previous) => ({ ...previous, metadata: { ...previous.metadata, terms: event.target.value } }))}
                                    rows={2}
                                    placeholder="VD: Áp dụng cho đơn hàng từ 200.000đ, không áp dụng cùng CTKM khác..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </Field>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => { setShowModal(false); setEditingId(null); }}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !form.name || !form.campaign_id}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Đang lưu...' : editingId ? 'Lưu thay đổi' : 'Tạo quà tặng'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {distributionGift && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Sử dụng: {distributionGift.name}</h3>
                                <p className="text-sm text-gray-500 font-mono">{distributionGift.code}</p>
                            </div>
                            <button
                                onClick={() => setDistributionGift(null)}
                                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Đóng
                            </button>
                        </div>

                        {distributionLoading ? (
                            <div className="py-12 text-center text-gray-500">Đang tải dữ liệu sử dụng...</div>
                        ) : distributions.length === 0 ? (
                            <div className="py-12 text-center text-gray-500">Chưa có phần thưởng nào được phát hoặc nhận.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <Th>Người chơi</Th>
                                            <Th>Mã thưởng</Th>
                                            <Th>Trạng thái</Th>
                                            <Th>Đã phát</Th>
                                            <Th>Đã nhận</Th>
                                            <Th>Hết hạn</Th>
                                            <Th align="center">Điểm</Th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {distributions.map((distribution) => (
                                            <tr key={distribution.id}>
                                                <td className="px-4 py-3 text-sm min-w-[220px]">
                                                    <div className="font-medium text-gray-900">
                                                        {distribution.player?.full_name || 'Người chơi ẩn danh'}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {distribution.player?.phone || distribution.player?.email || '-'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm font-mono">{distribution.reward_code || '-'}</td>
                                                <td className="px-4 py-3 text-sm">{distribution.status}</td>
                                                <td className="px-4 py-3 text-sm">{formatDateTime(distribution.assigned_at)}</td>
                                                <td className="px-4 py-3 text-sm">{formatDateTime(distribution.claimed_at)}</td>
                                                <td className="px-4 py-3 text-sm">{formatDate(distribution.expires_at)}</td>
                                                <td className="px-4 py-3 text-sm text-center">{distribution.play_session?.score ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone: string }) {
    return (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">{label}</p>
            <p className={`text-2xl font-bold ${tone}`}>{value}</p>
        </div>
    );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {children}
        </div>
    );
}

function Th({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'center' }) {
    return (
        <th className={`px-4 py-3 ${align === 'center' ? 'text-center' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>
            {children}
        </th>
    );
}
